import React, { useEffect } from "react";
import { useTheme } from "react-jss";
import numeral from "numeral";
import classNames from "classnames";
import ScrollableContainer from "../shared/ScrollableContainer";
import useStyles from "./PaginatedTable.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { TableFooter } from "./TableFooter";
import InputBox from "../shared/InputBox";
import { CheckBoxIcon } from "../shared/CheckBoxIcon";
import Button from "../shared/Button";
import ButtonGroup from "../shared/ButtonGroup";
import ModalDialog, { OkResult } from "../shared/ModalDialog";
import { AdvancedSettingsVersion } from "../../consts";
import useQuestion from "../../hooks/useQuestion";
import { useKeyboardNavigationForLookupTable } from "../../hooks/useKeyboardNavigationForLookupTable";
import { LookupTableNavigation } from "../controls/KeyboardNavigation/LookupTableNavigation";

export interface TableColumn {
    name: string;
    searchable?: boolean;
    width?: number;
    filter?: string;
    translatedName?: string;
}

export interface TableRow {
    id: number;
    selected?: boolean;
    [columnName: string]: any;
}

export interface ColumnFilterProps {
    value?: string;
    onChange?(value: string): void;
    name?: string;
}

export interface TableTexts {
    search: string;
    selectedItems: string;
    selectedItemsLimit: string;
    emptyTable: string;
    info: string;
    infoFiltered: string;
    lengthMenu: string;
    lookupTableAll: string;
    lookupTableSelected: string;
    lookupTableUnselected: string;
    lookupTableAddChoiceButtonLabel: string;
    cancelNewItemText: string;
    saveNewItemText: string;
    addChoicePopupTitle: string;
    confirmationButtonLabel: string;
    cancelButtonLabel: string;
}

export interface AdvancedSettings {
    respondentActions: {
        allow: boolean;
        access: number;
    };
    displayTabs: boolean;
    hideTableByDefault: boolean;
    hideTableHeader: boolean;
    hideTablePageNavigation: boolean;
    version: string;
    searchBarForAllColumns: boolean;
}

export interface NewItemFormProps {
    columns: TableColumn[];
    title: string;
    onCancel(): void;
    onSave(props: Record<string, string>): OkResult;
    onSuccess?(): void;
    isOpen: boolean;
    cancelNewItemText: string;
    saveNewItemText: string;
}

export interface TableHeaderProps {
    name: string;
    width: number;
    searchable: boolean;
    translatedName: string;
    sortColumn: string;
    sortAsc: boolean;
    classes: any;
    onChangeSort: (sortColumn: string, sortAsc: boolean) => void;
}

function NewItemAddForm(props: NewItemFormProps) {
    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const [inputValues, setInputValues] = React.useState<Record<string, string>>({});
    const [isDisableConfirm, setIsDisableConfirm] = React.useState(true);
    const { columns, isOpen, cancelNewItemText, saveNewItemText, title, onCancel } = props;
    const columnNames = columns.map(({ name }) => name);
    const handleInputChange = (e) => {
        setInputValues({
            ...inputValues,
            [e.target.name]: e.target.value,
        });
    };

    React.useEffect(() => {
        const allFieldsFilled = columnNames.every(
            (field) => Object.keys(inputValues).includes(field) && inputValues[field],
        );
        setIsDisableConfirm(!allFieldsFilled);
    }, [inputValues]);

    const saveForm = (): OkResult => {
        const result = props.onSave(inputValues);
        if (result.isSuccess) {
            setInputValues({});
            props.onSuccess();
        }
        return result;
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            cancelActionText={cancelNewItemText}
            okActionText={saveNewItemText}
            title={title}
            onCancelAction={onCancel}
            onOkAction={saveForm}
            isDisableConfirm={isDisableConfirm}
        >
            {columns.map((item) => {
                return (
                    <div className={classes.formRow} key={item.translatedName}>
                        <div className={classes.label}>{item.translatedName}</div>
                        <input
                            type="text"
                            name={item.name}
                            className={classes.inputField}
                            value={inputValues[item.name] || ""}
                            onChange={handleInputChange}
                        />
                    </div>
                );
            })}
        </ModalDialog>
    );
}

export interface SearchInputProps {
    setSearch: (value: string) => void;
    texts: TableTexts;
    search: string;
}

function RenderSearchInput(props: any) {
    const { setSearch, texts, search } = props;
    const question = useQuestion();
    const { elementReference, onKeyDown } = useKeyboardNavigationForLookupTable(
        question.id,
        `LOOKUP_TABLE_SEARCH_INPUT_${question.id}`,
        { id: `LOOKUP_TABLE_SEARCH_INPUT_${question.id}` },
        question,
        LookupTableNavigation,
        null,
        "lookupTableSearchInput",
    );

    return (
        <input
            type="text"
            id={`LOOKUP_TABLE_SEARCH_INPUT_${question.id}`}
            ref={elementReference}
            placeholder={texts.search}
            onKeyDown={onKeyDown}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
        />
    );
}

function RenderTableHeader(props: TableHeaderProps) {
    const { name, width, searchable, translatedName, onChangeSort, sortColumn, sortAsc, classes } = props;
    const question = useQuestion();
    const { elementReference, onKeyDown } = useKeyboardNavigationForLookupTable(
        question.id,
        `${name}_Sort`,
        { id: `${name}_Sort` },
        question,
        LookupTableNavigation,
        null,
        "lookupTableHeader",
    );

    return (
        <th
            key={name}
            tabIndex={0}
            role="button"
            onClick={() =>
                onChangeSort(
                    name,
                    sortColumn === name ? !sortAsc : true,
                )
            }
            onKeyDown={onKeyDown}
            ref={elementReference}
            style={{ minWidth: width, maxWidth: width }}
            className={classNames(
                classes.tableColumn,
                sortColumn === name && classes.sortedColumn,
            )}
        >
            {searchable && (
                <FontAwesomeIcon
                    icon={[
                        sortColumn === name && classes.sortedColumn
                            ? "fas"
                            : "far",
                        "search",
                    ]}
                    size="sm"
                    fixedWidth
                />
            )}
            <span id={`TH_${name.toUpperCase()}`}>
                {translatedName || name}
            </span>

            {sortColumn === name && (
                <FontAwesomeIcon
                    icon={sortAsc ? "angle-down" : "angle-up"}
                    fixedWidth
                />
            )}
        </th>
    );
};

function ColumnFilter(props: ColumnFilterProps) {
    const { value: _value, onChange, name } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });
    const prevValue = React.useRef(_value);
    const [value, setValue] = React.useState(_value);
    const onChangeRef = React.useRef(onChange);
    const question = useQuestion();
    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    React.useEffect(() => {
        if (prevValue.current === value) return undefined;
        prevValue.current = value;
        const timer = setTimeout(() => onChangeRef.current && onChangeRef.current(value), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [value]);
    const { elementReference, onKeyDown } = useKeyboardNavigationForLookupTable(
        question.id,
        `${name}_Filter`,
        { id: 0 },
        question,
        LookupTableNavigation,
        null,
        "lookupTableHeader",
    );
    return (
        <input
            type="text"
            className={classes.tableColumnFilterInput}
            value={value}
            ref={elementReference}
            onKeyDown={onKeyDown}
            onChange={(e) => setValue(e.target.value)}
            aria-labelledby={`TH_${name.toUpperCase()}`}
        />
    );
}

interface PaginatedTableRowProps {
    index: number;
    row: TableRow;
    tableProps: PaginatedTableProps;
}

function PaginatedTableRow(props: PaginatedTableRowProps) {
    const { index, row, tableProps } = props;
    const { selectable, columns, max, totalSelected, onChangeRowSelected } = tableProps;
    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const [hover, setHover] = React.useState(false);
    const question = useQuestion();
    const rowId = question.id + "_" + row.id;
    const { elementReference, onKeyDown } = useKeyboardNavigationForLookupTable(
        question.id,
        `${rowId}`,
        { id: row.id },
        question,
        LookupTableNavigation,
        null,
        "lookupTableRow",
    );

    return (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <tr
            ref={elementReference}
            id={rowId}
            key={row.id}
            role="button"
            aria-pressed={row.selected}
            onKeyDown={onKeyDown}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classNames(
                classes.tableRow,
                index % 2 === 1 && classes.tableRowAlternate,
                selectable && !row.disabled && classes.tableRowSelectable,
                row.selected && classes.tableRowSelected,
            )}
            onClick={(e) => {
                if (row.selected || !max || totalSelected < max || max === 1) {
                    onChangeRowSelected(row, !row.selected);
                    e.preventDefault();
                }
            }}
            tabIndex={0}
        >
            {selectable && theme.DisplayChoiceIcon === "left" && (
                <td className={classes.tableRowCheckbox}>
                    <CheckBoxIcon
                        checked={row.selected}
                        hover={hover}
                        disabled={!row.selected && max && max > 1 && totalSelected >= max}
                        backgroundless={theme.ControlAlpha === 0}
                    />
                </td>
            )}
            {columns.map(({ name, width, searchable }) => (
                <td
                    id={`${row.id}_${name}`}
                    key={name}
                    style={{ minWidth: width, maxWidth: width }}
                >
                    {row[name]}
                </td>
            ))}
            {selectable && theme.DisplayChoiceIcon === "right" && (
                <td className={classes.tableRowCheckbox}>
                    <CheckBoxIcon
                        checked={row.selected}
                        disabled={!row.selected && max && totalSelected >= max}
                        backgroundless={theme.ControlAlpha === 0}
                    />
                </td>
            )}
        </tr>
    );
}

export interface PaginatedTableProps {
    id: string;
    columns: TableColumn[];
    defaultRowsPerPage?: number;
    rowsPerPageOptions: number[];
    pageRows: TableRow[];
    pageStart: number;
    max?: number;
    selectable?: boolean;
    filterable?: boolean;
    sortColumn?: string;
    sortAsc?: boolean;
    totalRows: number;
    totalFilteredRows?: number;
    totalSelected?: number;
    search?: string;
    texts?: TableTexts;
    advancedSettings: AdvancedSettings;
    isShowPageInfo?: boolean;
    isShowRowsPerPage?: boolean;
    isAddNewItem?: boolean;

    onChangeRowSelected?(row: TableRow, selected: boolean): void;
    onChangeSort?(sortColumn: string, sortAsc: boolean): void;
    onChangePage?(pageStart: number, length: number): void;
    onChangeSearch?(value: string): void;
    onChangeColumnFilter?(column: string, value: string): void;
    onSelectedTabChange?(value: string): void;
    onItemAdd?(props: any): OkResult;
}

export function PaginatedTable(props: PaginatedTableProps) {
    const {
        columns,
        pageRows,
        pageStart,
        max,
        totalRows,
        totalFilteredRows,
        totalSelected,
        sortAsc,
        sortColumn,
        selectable,
        filterable,
        advancedSettings,
        onChangeSort,
        defaultRowsPerPage,
        rowsPerPageOptions,
        search: searchValue,
        texts,
        onChangeSearch,
        onChangeColumnFilter,
        onChangePage,
        onSelectedTabChange,
        onItemAdd,
        isShowPageInfo,
        isShowRowsPerPage,
        isAddNewItem,
    } = props;
    const initialRowsPerPage = defaultRowsPerPage || rowsPerPageOptions[0] || 10;
    const prevRowsPerPage = React.useRef(initialRowsPerPage);
    const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
    const onChangePageRef = React.useRef<(pageStart: number, length: number) => void>();

    const pageCount = Math.ceil(totalFilteredRows / rowsPerPage);
    const currentPage = Math.floor(pageStart / rowsPerPage) + 1;

    const prevSearch = React.useRef(searchValue);
    const [search, setSearch] = React.useState(searchValue);

    const theme: any = useTheme();
    const classes = useStyles({ theme });

    const [isSearchOn, setIsSearchOn] = React.useState(false);
    const [selectedTab, setSelectedTab] = React.useState("all");
    const [isGridOn, setIsGridOn] = React.useState(!advancedSettings.hideTableByDefault);
    const [isModalOn, setIsModalOn] = React.useState(false);

    React.useEffect(() => {
        if (prevSearch.current === search) return undefined;
        prevSearch.current = search;
        const timer = setTimeout(() => {
            onChangeSearch(search);
            if (advancedSettings.hideTableByDefault) {
                if (search) {
                    setIsGridOn(true);
                } else {
                    setIsGridOn(false);
                }
            }
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [search, onChangeSearch, advancedSettings.hideTableByDefault]);

    React.useEffect(() => {
        onChangePageRef.current = onChangePage;
    }, [onChangePage]);

    React.useEffect(() => {
        if (prevRowsPerPage.current === rowsPerPage) return;
        prevRowsPerPage.current = rowsPerPage;
        onChangePageRef.current(0, rowsPerPage);
    }, [rowsPerPage]);

    const hasSearchableColumns = columns.some((c) => c.searchable);

    const isSearchableColumn = (column: string): boolean => {
        return columns.some((c) => c.name === column && c.searchable);
    };

    const updateSelectedTab = (item) => {
        onSelectedTabChange(item);
        setSelectedTab(item);
    };

    const resetFilters = () => {
        setSearch("");
        updateSelectedTab("all");
    };

    const resetSearch = () => {
        setIsSearchOn(false);
        setSearch("");
    };

    const newVersion = advancedSettings.version === AdvancedSettingsVersion;

    const renderSearchBar =
        (!newVersion && hasSearchableColumns) ||
        (newVersion && advancedSettings.searchBarForAllColumns);

    const renderFilter = (!newVersion && filterable) || (newVersion && hasSearchableColumns);
    const question = useQuestion();

    const elementReference = React.useRef(null);
    const buttonElementReference = React.useRef(null);
    const searchBtnId = `${question.id}-SEARCH-TOGGLE`;
    useEffect(() => {
        if (isSearchOn) {
            elementReference.current.focus();
        }
    }, [isSearchOn, elementReference]);



    return (
        <div className={classes.paginatedTable}>
            {advancedSettings.displayTabs ? (
                <>
                    <div className={classes.gridHeaderButtons}>
                        {!isSearchOn && (
                            <ButtonGroup size="large" isGrid>
                                <ButtonGroup.Button
                                    active={selectedTab === "all"}
                                    text={texts.lookupTableAll}
                                    onClick={() => {
                                        updateSelectedTab("all");
                                    }}
                                    id={`${question.id}_ALL`}
                                />
                                <ButtonGroup.Button
                                    text={texts.lookupTableSelected}
                                    active={selectedTab === "selected"}
                                    onClick={() => {
                                        updateSelectedTab("selected");
                                    }}
                                    id={`${question.id}_SELECTED`}
                                />
                                <ButtonGroup.Button
                                    text={texts.lookupTableUnselected}
                                    active={selectedTab === "unselected"}
                                    onClick={() => {
                                        updateSelectedTab("unselected");
                                    }}
                                    id={`${question.id}_UNSELECTED`}
                                />
                            </ButtonGroup>
                        )}
                        {renderSearchBar && (
                            <div
                                className={classes.gridSearchZone}
                                style={{ width: isSearchOn && "100%" }}
                            >
                                <Button
                                    onClick={() => {
                                        setIsSearchOn(true);
                                    }}
                                    icon={["fas", "search"]}
                                    className={
                                        isSearchOn ? classes.hiddenItem : classes.searchToggleButton
                                    }
                                    buttonType="LOOKOUT_SEARCH_BTN"
                                    questionId={`${question.id}`}
                                    buttonId={searchBtnId}
                                    buttonElementRef={buttonElementReference}
                                />
                            </div>
                        )}
                    </div>
                    {isSearchOn && (
                        <InputBox accessoryLeft="search" size="100%">
                            <input
                                type="text"
                                id={`LOOKUP_TABLE_SEARCH_INPUT_${question.id}`}
                                ref={elementReference}
                                placeholder={texts.search}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <span
                                className={classes.inputWithButton}
                                onClick={resetSearch}
                                onKeyDown={(event) => {
                                    if (event.key === " ") {
                                        event.preventDefault();
                                        setTimeout(() => {
                                            buttonElementReference.current.focus();
                                        }, 100);
                                        resetSearch();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                <FontAwesomeIcon icon={["fas", "times"]} />
                            </span>
                        </InputBox>
                    )}
                </>
            ) : (
                renderSearchBar && (
                    <InputBox accessoryLeft="search" style={{ marginBottom: 8 }}>
                        <RenderSearchInput
                            setSearch={setSearch}
                            texts={texts}
                            search={search}
                        />
                    </InputBox>
                )
            )}
            <br />
            {isGridOn && (
                <ScrollableContainer>
                    <table className={classNames(classes.table, "grid-view")}>
                        {!advancedSettings.hideTableHeader && (
                            <thead>
                                <tr>
                                    {selectable && theme.DisplayChoiceIcon === "left" && (
                                        <th className={classes.tableColumn} />
                                    )}
                                    {columns.map(({ name, width, searchable, translatedName }) =>
                                        <RenderTableHeader
                                            key={name}
                                            name={name}
                                            width={width}
                                            searchable={searchable}
                                            translatedName={translatedName}
                                            sortColumn={sortColumn}
                                            classes={classes}
                                            sortAsc={sortAsc}
                                            onChangeSort={onChangeSort}
                                        />
                                    )}
                                    {selectable && theme.DisplayChoiceIcon === "right" && (
                                        <th className={classes.tableColumn} />
                                    )}
                                </tr>
                                {renderFilter && (
                                    <tr>
                                        {selectable && theme.DisplayChoiceIcon === "left" && (
                                            <th className={classes.tableColumn} />
                                        )}
                                        {columns.map(({ name, width, filter }) => (
                                            <th
                                                key={name}
                                                className={classes.tableColumnFilter}
                                                style={{ minWidth: width, maxWidth: width }}
                                            >
                                                {!newVersion ||
                                                    (newVersion && isSearchableColumn(name)) ? (
                                                    <ColumnFilter
                                                        value={filter || ""}
                                                        onChange={(value) =>
                                                            onChangeColumnFilter(name, value)
                                                        }
                                                        name={name}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                            </th>
                                        ))}
                                        {selectable && theme.DisplayChoiceIcon === "right" && (
                                            <th className={classes.tableColumn} />
                                        )}
                                    </tr>
                                )}
                            </thead>
                        )}
                        <tbody>
                            {pageRows.map((row, index) => (
                                <PaginatedTableRow
                                    key={row.id}
                                    index={index}
                                    row={row}
                                    tableProps={props}
                                />
                            ))}
                        </tbody>
                    </table>
                </ScrollableContainer>
            )}
            <TableFooter
                tableProps={props}
                rowsPerPage={rowsPerPage}
                pageCount={pageCount}
                currentPage={currentPage}
                lengthMenuText={texts.lengthMenu}
                onChangeRowsPerPage={setRowsPerPage}
                isShowPageInfo={isShowPageInfo}
                isShowRowsPerPage={isShowRowsPerPage}
                isAddNewItem={isAddNewItem}
                newItemLabel={texts.lookupTableAddChoiceButtonLabel}
                onItemAdd={() => {
                    setIsModalOn(true);
                }}
                isShowPagination={!advancedSettings.hideTablePageNavigation && isGridOn}
            >
                {!pageRows.length && texts.emptyTable}
                {pageRows.length > 0 &&
                    texts.info
                        .replace("_START_", `${pageStart + 1}`)
                        .replace("_END_", `${pageStart + pageRows.length}`)
                        .replace("_TOTAL_", numeral(totalFilteredRows).format("0,0"))}
                {totalFilteredRows < totalRows
                    ? ` ${texts.infoFiltered.replace("_MAX_", numeral(totalRows).format("0,0"))}`
                    : ""}
                {totalSelected
                    ? ` ${(max ? texts.selectedItemsLimit : texts.selectedItems)
                        .replace("{count}", `${totalSelected}`)
                        .replace("{limit}", `${max || ""}`)}`
                    : ""}
            </TableFooter>

            {isAddNewItem && (
                <NewItemAddForm
                    columns={columns}
                    onCancel={() => {
                        setIsModalOn(false);
                    }}
                    onSave={onItemAdd}
                    title={texts.addChoicePopupTitle ?? texts.lookupTableAddChoiceButtonLabel}
                    isOpen={isModalOn}
                    cancelNewItemText={texts.cancelButtonLabel ?? texts.cancelNewItemText}
                    saveNewItemText={texts.confirmationButtonLabel ?? texts.saveNewItemText}
                    onSuccess={resetFilters}
                />
            )}
        </div>
    );
}
